import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { FormControl, InputAdornment } from '@mui/material'
import ReactDatePicker from 'react-datepicker'
import { format, parse } from 'date-fns'

import { CalendarIcon } from '@assets/svgs/CalendarIcon'
import { HeaderDate } from '../HeaderDate'

import type { CustomInputProps, ComponentProps } from './types'

import './styles.css'
import * as S from './styles'

export const Component = ({
  name,
  label = 'Data',
  required,
  value,
  onChange,
  disabled,
  error,
  hasIcon = true,
  placeholder = 'Selecione uma data',
  minDate: minDateProp,
  maxDate: maxDateProp,
  ...rest
}: ComponentProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const isActive = useMemo(() => {
    return isFocused || Boolean(selectedDate)
  }, [isFocused, selectedDate])

  const minDateDefault = new Date(1900, 0, 1)
  const maxDateDefault = new Date(2100, 11, 31)
  const minDate = minDateProp ?? minDateDefault
  const maxDate = maxDateProp ?? maxDateDefault

  useEffect(() => {
    if (!value) {
      setSelectedDate(null)
      return
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      setSelectedDate(value)
      return
    }

    if (typeof value === 'string') {
      const parsedDate = parse(value, 'dd/MM/yyyy', new Date())
      if (!Number.isNaN(parsedDate.getTime())) {
        setSelectedDate(parsedDate)
        return
      }
    }

    setSelectedDate(null)
  }, [value])

  const inputValue = typeof value === 'string' ? value : ''

  const getPopperPlacement = (): 'bottom-start' | 'bottom-end' => {
    if (!containerRef.current) return 'bottom-start'

    const input = containerRef.current.querySelector('input') as HTMLInputElement
    if (!input) return 'bottom-start'

    const inputRect = input.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const popperWidth = 280
    const margin = 8

    if (inputRect.left + popperWidth + margin > viewportWidth) {
      return 'bottom-end'
    }

    return 'bottom-start'
  }

  const [popperPlacement, setPopperPlacement] = useState<'bottom-start' | 'bottom-end'>('bottom-start')

  const handleFocus = () => {
    if (!disabled) {
      setPopperPlacement(getPopperPlacement())
      setIsFocused(true)
    }
  }

  return (
    <FormControl disabled={disabled} fullWidth ref={containerRef}>
      <S.Label disabled={disabled}>
        {label}
        {required && '*'}
      </S.Label>

      <ReactDatePicker
        id={name}
        name={name}
        locale="pt-BR"
        selected={selectedDate}
        value={inputValue}
        onChange={(date: Date | null) => {
          if (disabled) return
          setSelectedDate(date)
          onChange(date ? format(date, 'dd/MM/yyyy') : '')
        }}
        shouldCloseOnSelect={true}
        minDate={minDate}
        maxDate={maxDate}
        dateFormat="dd/MM/yyyy"
        placeholderText={placeholder}
        calendarClassName="date-picker-calendar"
        popperClassName="date-picker-popper"
        popperPlacement={popperPlacement}
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3).charAt(0).toUpperCase() + nameOfDay.substring(1, 3)}
        onFocus={handleFocus}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          changeYear,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled
        }) => (
          <HeaderDate
            date={date}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
            changeYear={changeYear}
            prevMonthButtonDisabled={prevMonthButtonDisabled}
            nextMonthButtonDisabled={nextMonthButtonDisabled}
          />
        )}
        customInput={
          <CustomInput
            isActive={isActive}
            disabled={disabled}
            error={error}
            hasIcon={hasIcon}
            placeholder={placeholder}
            onFocus={handleFocus}
          />
        }
        {...rest}
      />

      {typeof error === 'string' && <S.ErrorMessage>{error}</S.ErrorMessage>}
    </FormControl>
  )
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ value, onClick, onFocus, onBlur, disabled, error, hasIcon, placeholder }, ref) => {
    const formattedValue = value ?? ''

    return (
      <S.TextField
        value={formattedValue}
        onClick={disabled ? undefined : onClick}
        onFocus={disabled ? undefined : onFocus}
        onBlur={onBlur}
        inputRef={ref}
        fullWidth
        size="medium"
        disabled={disabled}
        placeholder={placeholder}
        error={!!error}
        InputProps={{
          endAdornment: hasIcon && (
            <InputAdornment position="end">
              <S.IconWrapper error={!!error} disabled={disabled}>
                <CalendarIcon />
              </S.IconWrapper>
            </InputAdornment>
          )
        }}
      />
    )
  }
)

CustomInput.displayName = 'CustomInput'
